import { useEffect, useRef, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useWindowSize } from 'react-use';
import { useQuery, useQueryClient } from 'react-query';
import { _ToastMessageProps, _VenuePickerOption } from '../../../../constants/staticTypes';
import { defaultToastData } from '../../../../helpers/commonHelper';
import MlxButton from '../../../../components/common/MlxButton/MlxButton';
import withSidebar from '../../../../hoc/withSideBar/withSideBar';
import { getVenueOptions } from '../../../../helpers/venueHelper';
import {
    ADD_VENUE,
    AREA_OF_OPERATION,
    EMPLOYEE_WORK_TIME_SCHEDULE,
    MANAGE_VENUE,
    SELECT_VENUE,
    USERS_LIST,
    VENUE_LIST,
} from '../../../../constants/strings';
import { useTranslation } from '../../../../hooks/useTranslation';
import { isEmptyObject, toPascalCase } from '../../../../utils/common';
import MlxDropDown from '../../../../components/common/MlxDropDown/MlxDropDown';
import ScheduleDesktopView from './components/ScheduleDesktopView';
import dayjs from 'dayjs';
import {
    fetchDraftedAndPublishedScheduleIds,
    fetchStaffsWithSchedulesAndShift,
    _VenueStaffOption,
    getUserRoles,
    getVenueStaffOptions,
    getWeekRange,
    JobRole,
    PrimeReactDropDown,
    PublishedSchedule,
    ScheduleData,
    ScheduleDepartmentDropDown,
    ScheduleIdsResponse,
    ScheduleMetadata,
} from '../../../../helpers/venueScheduleHelper';
import './venueSchedule.scss';
import { AxiosResponse } from 'axios';
// import { isEmptyObject } from 'jquery';
import { STAFFS_WITH_SCHEDULES_AND_SHIFT, SCHEDULE_STATUS, USER_ROLES } from '../../../../constants/queryKeys';
import { DropdownChangeEvent } from 'primereact/dropdown';
import FilterWrapper from './components/FilterWrapper';
import WeekFilter from './components/WeekFilter';
import MlxMultiSelect from '../../../../components/common/MlxMultiSelect/MlxMultiSelect';
import MlxBlockUI from '../../../../components/common/MlxBlockUI/MlxBlockUI';
import { capitalize, cloneDeep } from 'lodash';
import MlxDialog from '../../../../components/common/MlxDialog/MlxDialog';
import PrintPreview from './components/PrintPreview';

const searchColumns = ['tenantName'];

const languageTexts = {
    MANAGE_VENUE: MANAGE_VENUE,
    VENUE_LIST: VENUE_LIST,
    ADD_VENUE: ADD_VENUE,
    SELECT_VENUE: SELECT_VENUE,
    EMPLOYEE_WORK_TIME_SCHEDULE: EMPLOYEE_WORK_TIME_SCHEDULE,
    AREA_OF_OPERATION: AREA_OF_OPERATION,
};

function VenueScheduleManagement() {
    const { width: screenWidth } = useWindowSize();
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'none');

    const [isVenueListLoading, setIsVenueListLoading] = useState<boolean>(false);
    const [venueOptions, setVenueOptions] = useState<_VenuePickerOption[]>([]);
    const [selectedVenueOption, setSelectedVenueOption] = useState<string>('');

    const scheduleDialogRef = useRef(null);

    const [selectedWeek, setSelectedWeek] = useState<dayjs.Dayjs>(dayjs());
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [scheduleData, setScheduleData] = useState<PublishedSchedule[]>([]);
    const [scheduleFilteredData, setScheduleFilteredData] = useState<PublishedSchedule[]>([]);
    const [mobileScheduleData, setMobileScheduleData] = useState<PublishedSchedule[]>([]);
    const [mobileScheduleFilteredData, setMobileScheduleFilteredData] = useState<PublishedSchedule[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const dateRange = getWeekRange(selectedWeek); // Calculate the date range for the selected week
    const [staffsWithSchedule, setStaffsWithSchedule] = useState<ScheduleData | null>(null);
    const [staffsWithScheduleFiltered, setStaffsWithScheduleFiltered] = useState<ScheduleData | null>(null);
    const [publishedScheduleId, setPublishedScheduleId] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [departments, setDepartments] = useState<ScheduleDepartmentDropDown[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<ScheduleDepartmentDropDown | null>(null);
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [scheduleMetadata, setScheduleMetadata] = useState<ScheduleMetadata | null>(null);
    const [amPmView, setAmPmView] = useState<boolean>(true);
    const [rolesList, setRolesList] = useState<any[]>(); // roles list
    const [filteredUsers, setFilteredUsers] = useState<string[]>([]);
    const [usersListOptions, setUsersListOptions] = useState<PrimeReactDropDown[]>([]);
    const [baseStaffsWithSchedule, setBaseStaffsWithSchedule] = useState<ScheduleData | null>(null);

    useEffect(() => {
        if (staffsWithSchedule) {
            setBaseStaffsWithSchedule(cloneDeep(staffsWithSchedule));
        }
    }, [staffsWithSchedule]);

    useEffect(() => {
        setIsVenueListLoading(true);
        const getVenues = async () => {
            const venueOptionsList = await getVenueOptions(queryClient);
            setVenueOptions(venueOptionsList);
            venueOptionsList?.length > 0 && setSelectedVenueOption(venueOptionsList[0].id);
            setIsVenueListLoading(false);
        };
        getVenues();
    }, []);

    const onRoleListSuccess = (res: AxiosResponse<any, any>) => {
        setRolesList(res.data?.data.lists);
        let data: JobRole[] | [] = res.data?.data.lists;
        let departments: ScheduleDepartmentDropDown[] = [];

        data.forEach((item) => {
            const { jobRoleDepartment } = item;

            // if (!isAvailableForSchedule) return;

            if (jobRoleDepartment.departmentName) {
                departments.push({
                    departmentId: jobRoleDepartment.id,
                    depName: jobRoleDepartment.departmentName ?? '',
                });
            }
        });

        // Use Set to remove duplicates based on the department ID
        const uniqueDepartments = Array.from(new Set(departments.map((dep) => dep.departmentId))).map(
            (code) => departments.find((dep) => dep.departmentId === code)!
        );
        setDepartments(uniqueDepartments);
    };

    const userRolesQuery = useQuery({
        queryKey: USER_ROLES,
        queryFn: () => getUserRoles(selectedVenueOption),
        onSuccess: onRoleListSuccess,
        enabled: false,
        refetchOnWindowFocus: false,
    });

    const createUserOptions = ({ usersList }: { usersList: _VenueStaffOption[] }) => {
        const filteredUsersList = usersList
            .filter((user: _VenueStaffOption) => user.isActive)
            .map((user: _VenueStaffOption) => {
                const { id, preferredName, jobRoles = [] } = user;

                let isAvailableForSchedule = true;
                if (jobRoles.length === 1) {
                    isAvailableForSchedule = 'isAvailableForSchedule' in jobRoles[0] ? !!jobRoles[0].isAvailableForSchedule : true;
                }

                return { code: id, name: capitalize(preferredName ?? ''), isAvailableForSchedule };
            })
            .sort((a: PrimeReactDropDown, b: PrimeReactDropDown) => a.name.localeCompare(b.name));
        setUsersListOptions(filteredUsersList);
    };

    const onUserListFetchSuccess = (res: AxiosResponse<any, any>) => {
        const responseData = (res?.data?.data?.lists ?? []) as _VenueStaffOption[];
        createUserOptions({ usersList: responseData });
    };

    const usersListQuery = useQuery({
        queryKey: USERS_LIST,
        queryFn: () => getVenueStaffOptions(selectedVenueOption),
        onSuccess: onUserListFetchSuccess,
        enabled: false,
        refetchOnWindowFocus: false,
    });

    const onSuccessOfScheduleStatus = (res: AxiosResponse<any, any>) => {
        const scheduleData: ScheduleIdsResponse = res.data.data;
        const { published } = scheduleData;

        // Check if draft and published schedules are empty
        const isPublishedEmpty = isEmptyObject(published);

        // If published is not empty, save the published's id using savePublishedScheduleId
        if (!isPublishedEmpty) {
            setPublishedScheduleId(published.id);
        } else {
            setStaffsWithScheduleFiltered(null);
            setStaffsWithSchedule(null);
            setPublishedScheduleId('');
        }
    };

    const onSuccessOfStaffsWithSchedule = (res: AxiosResponse<any, any>) => {
        const sortStaffByPreferredName = (staffsWithSchedule: ScheduleData) => {
            // Separate staffs into two groups: with shifts and without shifts
            const staffsWithShifts = staffsWithSchedule.staffs.filter((staff) => Object.keys(staff.shifts).length > 0);
            const staffsWithoutShifts = staffsWithSchedule.staffs.filter((staff) => Object.keys(staff.shifts).length === 0);

            // Sort each group by preferredName
            staffsWithShifts.sort((a, b) => a.preferredName.localeCompare(b.preferredName));
            staffsWithoutShifts.sort((a, b) => a.preferredName.localeCompare(b.preferredName));

            // Combine the sorted arrays, with staff with shifts first
            const sortedStaffs = [...staffsWithShifts, ...staffsWithoutShifts];

            // Update the staffs in the original object
            return { ...staffsWithSchedule, staffs: sortedStaffs };
        };

        const staffsWithSchedule: ScheduleData = res.data.data;
        const sortedStaffsWithSchedule = sortStaffByPreferredName(staffsWithSchedule);

        setStaffsWithSchedule(sortedStaffsWithSchedule);
        setStaffsWithScheduleFiltered(sortedStaffsWithSchedule);
        setScheduleMetadata(staffsWithSchedule?.schedule?.scheduleMetadata ?? ({} as ScheduleMetadata));
    };

    // schedule data main QUERY-1
    const scheduleDataQuery = useQuery({
        queryKey: SCHEDULE_STATUS,
        queryFn: () => fetchDraftedAndPublishedScheduleIds(dateRange, selectedVenueOption),
        onSuccess: onSuccessOfScheduleStatus,
        onError: (error) => console.log(`Error fetching draft and published schedule ids.`, error),
        enabled: false,
        refetchOnWindowFocus: false,
    });

    // schedule data main QUERY-2
    const staffsWithScheduleQuery = useQuery({
        queryKey: STAFFS_WITH_SCHEDULES_AND_SHIFT,
        queryFn: () => fetchStaffsWithSchedulesAndShift({ scheduleId: publishedScheduleId }, selectedVenueOption),
        onSuccess: onSuccessOfStaffsWithSchedule,
        onError: (error) => console.log(`Error fetching schedule`, error),
        enabled: false,
        refetchOnWindowFocus: false,
    });

    // refetch the published schedule api once the selected date has changed
    useEffect(() => {
        if (!selectedVenueOption) return;
        setFilteredUsers([]);
        setSelectedDepartment(null);
        setStaffsWithScheduleFiltered(null);
        userRolesQuery.refetch();
        usersListQuery.refetch();
        scheduleDataQuery.refetch();
    }, [selectedWeek, selectedVenueOption]); // eslint-disable-line

    useEffect(() => {
        if (publishedScheduleId) {
            staffsWithScheduleQuery.refetch();
        }
    }, [publishedScheduleId]);

    const getRoleColorCode = (roleId: string) => {
        const role = rolesList?.find((role) => role.id === roleId);

        if (role?.styleDetails) {
            return role.styleDetails;
        } else {
            return {
                backgroundColor: '#87b6fb',
                color: '#262930',
            };
        }
    };

    const applyFilters = () => {
        if (!baseStaffsWithSchedule) return;

        let filtered = cloneDeep(baseStaffsWithSchedule);

        // User filtering
        if (filteredUsers?.length) {
            filtered.staffs = filtered.staffs.filter((staff) => filteredUsers.includes(staff.id));
        }

        // Department filtering
        if (selectedDepartment?.departmentId) {
            filtered.staffs = filtered.staffs.map((staff) => {
                const filteredShifts: any = {};
                for (const shiftKey in staff.shifts) {
                    filteredShifts[shiftKey] = staff.shifts[shiftKey].filter(
                        (shift) => shift.assignedShifts?.departmentId === selectedDepartment.departmentId
                    );
                }
                return { ...staff, shifts: filteredShifts };
            });
        }

        setStaffsWithScheduleFiltered(filtered);
    };

    useEffect(() => {
        applyFilters();
    }, [filteredUsers, selectedDepartment, baseStaffsWithSchedule]);

    useEffect(() => {
        if (!baseStaffsWithSchedule) return;

        // ScheduleData
        if (selectedDepartment) {
            setScheduleFilteredData(
                cloneDeep(scheduleData).map((schedule) => {
                    const filteredShifts = (schedule as any)?.shifts?.filter(
                        (shift: any) => shift?.assignedShifts?.departmentId === selectedDepartment.departmentId
                    );
                    return {
                        ...schedule,
                        shifts: filteredShifts,
                    };
                })
            );

            setMobileScheduleFilteredData(
                cloneDeep(mobileScheduleData).filter(
                    (shift: any) => shift?.assignedShifts?.departmentId === selectedDepartment.departmentId
                )
            );
        } else {
            setScheduleFilteredData(scheduleData);
            setMobileScheduleFilteredData(mobileScheduleData);
        }
    }, [selectedDepartment, scheduleData, mobileScheduleData]);

    return (
        <>
            <MlxDialog
                ref={scheduleDialogRef}
                visible={showPrintPreview}
                maximized={true}
                showHeader={false}
                onHide={() => {
                    if (!showPrintPreview) return;
                    setShowPrintPreview(false);
                }}
                pt={{
                    content: {
                        style: { padding: '0px' },
                    },
                }}
            >
                <div className="h-100 w-100">
                    <PrintPreview
                        setShowPrintPreview={setShowPrintPreview}
                        scheduleDialogRef={scheduleDialogRef}
                        staffsWithSchedule={staffsWithSchedule}
                        scheduleMetadata={scheduleMetadata}
                        selectedWeek={selectedWeek}
                        amPmView={amPmView ? 'amPm' : 'normal'}
                    />
                </div>
            </MlxDialog>
            <div className="page-section">
                <span className="page-header d-flex justify-content-between">
                    <span>{translatedText.EMPLOYEE_WORK_TIME_SCHEDULE}</span>
                    <div className="d-flex">
                        <div className="d-flex align-items-center">
                            <div className="align-items-center">
                                {/* Fullscreen preview */}
                                {/* {screenWidth >= 670 && (
                                    <MlxButton
                                        icon="ri-fullscreen-line"
                                        className=" p-button-rounded p-button-primary p-button-text fs-4 color-primary-custom"
                                        title="Fullscreen"
                                        onClick={() => {
                                            setShowPreview(true);
                                        }}
                                    />
                                )} */}

                                {/* AM/PM view */}
                                {
                                    <MlxButton
                                        icon="ri-calendar-todo-line "
                                        className={`me-1 p-button-rounded p-button-primary p-button-text fs-4 color-primary-custom ${amPmView ? 'bg-primary-active' : ''}`}
                                        title={'AM/PM View'}
                                        data-testid="am-pm-view-button"
                                        onClick={() => setAmPmView(!amPmView)}
                                        style={{ height: 'auto' }}
                                    />
                                }
                            </div>
                        </div>
                        <MlxDropDown
                            dropDownSize="large"
                            options={venueOptions}
                            optionLabel="venueName"
                            optionValue="id"
                            value={selectedVenueOption}
                            placeholder={translatedText.SELECT_VENUE}
                            onChange={(e) => {
                                setSelectedVenueOption(e.value);
                            }}
                            style={{ width: '200px' }}
                            disabled={venueOptions?.length <= 1}
                        />
                    </div>
                </span>
                <div className="page-content h-100">
                    <div className="content-header d-flex justify-content-between flex-grow-0">
                        <div className="d-flex">
                            <MlxMultiSelect
                                className="me-2 search-dropdown"
                                data-testid="users-select"
                                value={filteredUsers}
                                options={usersListOptions}
                                itemTemplate={(option: any) => (
                                    <div className="d-flex flex-row align-items-center justify-content-between" key={option.code}>
                                        <span>{toPascalCase(option.name)}</span>
                                        {!option.isAvailableForSchedule && <i className="ri-calendar-close-line text-danger ms-2"></i>}
                                    </div>
                                )}
                                onChange={(e) => {
                                    setFilteredUsers(e.value);
                                }}
                                panelClassName={'custom-multiselect'}
                                optionLabel="name"
                                showSelectAll={true}
                                maxSelectedLabels={1}
                                optionValue="code"
                                filter={true}
                                pt={{
                                    root: { style: { maxWidth: '42px', borderRadius: '3px' } },
                                    header: { style: { padding: '10px' } },
                                    filterContainer: { className: 'custom-multiselect-filter-container' },
                                    closeButton: { style: { display: 'none' } },
                                    // labelContainer: { style: { display: 'none' } }
                                }}
                                panelStyle={{ maxWidth: '250px' }}
                                resetFilterOnHide={true}
                                optionDisabled={(option: any) => !option.isAvailableForSchedule}
                                showClear={false}
                                placeholder={''}
                                selectedItemsLabel={''}
                                dropdownIcon={
                                    filteredUsers.length > 0 ? (
                                        <i className="pi pi-users" style={{ fontSize: '1.15rem', fontWeight: '500' }}></i>
                                    ) : (
                                        <i className="pi pi-search" style={{ fontSize: '1.15rem', fontWeight: '500' }}></i>
                                    )
                                }
                                panelFooterTemplate={
                                    <div className="px-2 py-2 shadow-lg d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            {filteredUsers.length > 0 &&
                                                `${filteredUsers.length} user${filteredUsers.length > 1 ? 's' : ''}`}
                                        </div>
                                        <MlxButton
                                            label="clear"
                                            className="btn btn-custom-primary fw-normal"
                                            onClick={() => setFilteredUsers([])}
                                            size="small"
                                            style={{ padding: '0.25rem 0.5rem' }}
                                        />
                                    </div>
                                }
                            />

                            <FilterWrapper
                                isFiltered={!!selectedDepartment}
                                clearFilter={() => {
                                    setSelectedDepartment(null);
                                }}
                            >
                                <MlxDropDown
                                    value={selectedDepartment ?? null}
                                    onChange={(e: DropdownChangeEvent) => {
                                        setSelectedDepartment(e.value ?? null);
                                    }}
                                    options={departments}
                                    optionLabel="name"
                                    placeholder={translatedText.AREA_OF_OPERATION}
                                    filter
                                    filterBy="depName"
                                    showClear
                                    disabled={!departments.length}
                                    valueTemplate={(option, props) => {
                                        if (option) {
                                            return (
                                                <div className="flex align-items-center">
                                                    <div>{option.depName}</div>
                                                </div>
                                            );
                                        }
                                        return <span>{props.placeholder}</span>;
                                    }}
                                    itemTemplate={(option) => (
                                        <div className="flex align-items-center">
                                            <div>{option.depName}</div>
                                        </div>
                                    )}
                                    className="w-full md:w-14rem"
                                    panelClassName="full-screen-dropdown-panel"
                                    pt={{ input: { style: { minWidth: '100px', paddingTop: '0.365rem', paddingBottom: '0.365rem' } } }}
                                />
                            </FilterWrapper>
                        </div>

                        <div className="d-flex">
                            <span className="ms-3">
                                <WeekFilter
                                    setPrevSelectedWeek={setSelectedWeek}
                                    setNextSelectedWeek={setSelectedWeek}
                                    selectedWeek={selectedWeek}
                                    onChange={(date, dateString) => {
                                        const selectedDate = date ? dayjs(date) : dayjs();
                                        setSelectedWeek(selectedDate);
                                    }}
                                />
                            </span>

                            <div className="cldr-action-icon ms-2">
                                <i
                                    className={`pi pi-print ${
                                        staffsWithScheduleQuery.isFetching || scheduleDataQuery.isFetching ? 'disabled' : ''
                                    }`}
                                    role="button"
                                    title={'Print Preview'}
                                    onClick={() => setShowPrintPreview(!showPrintPreview)}
                                ></i>
                            </div>

                            {screenWidth >= 670 && (
                                <MlxButton
                                    hidden={!showPreview}
                                    rounded
                                    outlined
                                    icon="ri-fullscreen-exit-line"
                                    className="ms-3 p-button-primary color-primary-custom"
                                    title="Close Preview"
                                    onClick={() => {
                                        setShowPreview(false);
                                    }}
                                    pt={{
                                        root: { style: { height: '2.5rem', width: '2.5rem' } },
                                        icon: { style: { fontSize: '1.5rem' } },
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <Divider className="content-divider" />
                    <div className="content-body flex-grow-1 overflow-hidden p-2">
                        <MlxBlockUI
                            isLoading={
                                staffsWithScheduleQuery.isFetching ||
                                scheduleDataQuery.isFetching ||
                                userRolesQuery.isFetching ||
                                isVenueListLoading
                            }
                        >
                            <ScheduleDesktopView
                                isLoading={
                                    staffsWithScheduleQuery.isRefetching ||
                                    scheduleDataQuery.isRefetching ||
                                    userRolesQuery.isFetching ||
                                    isVenueListLoading
                                }
                                selectedWeek={selectedWeek}
                                staffsWithSchedule={staffsWithScheduleFiltered}
                                getRoleColorCode={getRoleColorCode}
                                scheduleMetadata={scheduleMetadata}
                                viewType={amPmView ? 'amPm' : 'normal'}
                            />
                        </MlxBlockUI>
                    </div>
                </div>
            </div>
        </>
    );
}

export default withSidebar(VenueScheduleManagement);
